import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  Button,
  TextField,
  AppBar,
  Toolbar,
  Typography,
  Paper,
  CircularProgress,
  Modal,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import LoadingBar from "react-top-loading-bar";

const EditLocation = () => {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const navigate = useNavigate();
  const { state } = useLocation();
  const { locationId } = useParams();
  const encodedFormId = state?.encodedFormId || "";
  const [locationName, setLocationName] = useState("");
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [isBranchDisabled, setIsBranchDisabled] = useState(false);
  const [loadingBarProgress, setLoadingBarProgress] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);

  const fetchLocationDetails = async () => {
    try {
      setLoadingBarProgress(30);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(
        `${apiUrl}/Inventory/locations/GetLocations`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
          body: JSON.stringify({ locationId: parseInt(locationId) }),
        }
      );

      if (response.ok) {
        const responseData = await response.json();
        const location = responseData.locations?.[0];
        if (location) {
          setLocationName(location.locationName || "");
          setSelectedBranch(location.branchID || "");
        } else {
          setError("Location not found");
        }
      } else {
        let errorMsg = "Failed to fetch location details";
        if (response.status === 400) errorMsg = "Invalid location ID";
        else if (response.status === 401)
          errorMsg = "Unauthorized: Please log in again";
        else if (response.status === 403)
          errorMsg = "Forbidden: You lack permission to view locations";
        else if (response.status === 404) errorMsg = "Location not found";
        else if (response.status >= 500)
          errorMsg = "Server error: Please try again later";
        setError(errorMsg);
      }
    } catch (error) {
      console.error("Error fetching location details:", error);
      setError("Network error: Unable to connect to the server");
    } finally {
      setLoadingBarProgress(100);
      setIsLoading(false);
    }
  };

  const fetchBranches = async () => {
    try {
      setLoadingBarProgress(30);
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/Branch/GetBranch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: sessionStorage.getItem("token"),
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const responseData = await response.json();
        setBranches(responseData || []);
        if (responseData.length === 1) {
          setSelectedBranch(responseData[0].branchId); // Use branchId
          setIsBranchDisabled(true);
        }
      } else {
        let errorMsg = "Failed to fetch branches";
        if (response.status === 400) errorMsg = "Invalid request";
        else if (response.status === 401)
          errorMsg = "Unauthorized: Please log in again";
        else if (response.status === 403)
          errorMsg = "Forbidden: You lack permission to view branches";
        else if (response.status === 404) errorMsg = "Branches not found";
        else if (response.status >= 500)
          errorMsg = "Server error: Please try again later";
        setError(errorMsg);
      }
    } catch (error) {
      console.error("Error fetching branches:", error);
      setError("Network error: Unable to connect to the server");
    } finally {
      setLoadingBarProgress(100);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchBranches(), fetchLocationDetails()]);
    };
    loadData();
  }, [locationId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!locationName.trim()) {
      setError("Location Name is required");
      return;
    }
    if (!selectedBranch) {
      setError("Branch selection is required");
      return;
    }

    setIsSubmitting(true);
    setError("");
    setLoadingBarProgress(30);

    try {
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(
        `${apiUrl}/Inventory/locations/InsertUpdateLocation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: sessionStorage.getItem("token"),
          },
          body: JSON.stringify({
            locationID: parseInt(locationId),
            locationName: locationName.trim(),
            branchID: selectedBranch, // Ensure this matches API expectation
          }),
        }
      );

      if (response.ok) {
setShowSuccessModal(true);
      } else {
        let errorMsg = "Failed to update location";
        if (response.status === 400) errorMsg = "Invalid data provided";
        else if (response.status === 401)
          errorMsg = "Unauthorized: Please log in again";
        else if (response.status === 403)
          errorMsg = "Forbidden: You lack permission to update locations";
        else if (response.status === 404) errorMsg = "Location not found";
        else if (response.status >= 500)
          errorMsg = "Server error: Please try again later";
        setError(errorMsg);
      }
    } catch (error) {
      console.error("Error updating location:", error);
      setError("Network error: Unable to connect to the server");
    } finally {
      setLoadingBarProgress(100);
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleCloseSuccessDialog = () => {
    setOpenSuccessDialog(false);
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto mt-4">
        <LoadingBar
          progress={loadingBarProgress}
          color="rgb(152, 106, 182)"
          height={5}
        />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight={200}
        >
          <CircularProgress />
        </Box>
      </div>
    );
  }

  return (
    <div className="container mx-auto mt-4">
      <LoadingBar
        progress={loadingBarProgress}
        color="rgb(152, 106, 182)"
        height={5}
      />
      <AppBar position="static" style={{ backgroundColor: "#0B1F3D" }}>
        <Toolbar>
          <Typography variant="h4" component="div" style={{ fontWeight: 'bold' }}>
            Edit Location
          </Typography>
        </Toolbar>
      </AppBar>

      <Paper
        elevation={3}
        style={{
          padding: 16,
          paddingTop: 29,
          marginTop: 6,
          maxWidth: '100%',
          margin: "16px auto",
        }}
      >
        <form onSubmit={handleSubmit}>

          {error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}
          <TextField
            label="Location Name"
            variant="outlined"
            fullWidth
            value={locationName}
            onChange={(e) => setLocationName(e.target.value)}
            required
            sx={{ mb: 3 }}
            error={!!error && !locationName.trim()}
            helperText={
              !!error && !locationName.trim() ? "Location Name is required" : ""
            }
          />
          <FormControl
            fullWidth
            sx={{ mb: 3 }}
            error={!!error && !selectedBranch}
          >
            <InputLabel id="branch-select-label">Branch</InputLabel>
            <Select
              labelId="branch-select-label"
              value={selectedBranch}
              label="Branch"
              onChange={(e) => setSelectedBranch(e.target.value)}
              disabled={isBranchDisabled || isSubmitting}
              required
            >
              {branches.length === 0 && (
                <MenuItem value="" disabled>
                  No branches available
                </MenuItem>
              )}
              {branches.map((branch) => (
                <MenuItem key={branch.branchId} value={branch.branchId}>
                  {branch.branchName}
                </MenuItem>
              ))}
            </Select>
            {!!error && !selectedBranch && (
              <Typography color="error" variant="caption">
                Branch selection is required
              </Typography>
            )}
          </FormControl>
          <Box display="flex" justifyContent="flex-end" gap={2}>
           
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
              style={{
                width: "80px",
                height: "50px",
                padding: "10px 0",
                fontWeight: 'bold'
              }}
            >
              UPDATE
            </Button>
             <Button
              variant="contained"
              color="error"
              onClick={handleCancel}
              disabled={isSubmitting}
              style={{
                width: "80px",
                height: "50px",
                padding: "10px 0",
                fontWeight: 'bold'
              }}

            >
              CANCEL
            </Button>
          </Box>
        </form>
      </Paper>

<Modal
  open={showSuccessModal}
  onClose={() => setShowSuccessModal(false)}
  aria-labelledby="success-modal-title"
  aria-describedby="success-modal-description"
>
  <Box
    sx={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: 400,
      bgcolor: 'background.paper',
      borderRadius: 2,
      boxShadow: 24,
      overflow: 'hidden',
    }}
  >
    {/* Header */}
    <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
      <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
       Location Updated ✅
      </Typography>
    </Box>

    {/* Content */}
    <Box sx={{ padding: '20px' }}>
      <Typography id="success-modal-description" sx={{ mb: 2 }}>
        The Location has been updated successfully.
      </Typography>

      {/* Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button
          onClick={() => {
            setShowSuccessModal(false);
            navigate(-1); // go back after closing modal
          }}
          variant="contained"
          color="primary"
        >
          OK
        </Button>
      </Box>
    </Box>
  </Box>
</Modal>

    </div>
  );
};

export default EditLocation;
